import type { FlowNodeEntity } from '@flowgram.ai/free-layout-editor';
import { createAsset } from '$/api/canvasRquest';
import type { AssetCreateParams } from '$/api/server.types';
import { sanitizeFilename } from '$/utils/download';
import { mtstatTrack } from '$/utils/mtstat';
import {
  blobToFile, dealUploadFile, getVideoCoverUrl, type CallbackFiles,
} from '$/utils/upload';
import useFlowNextStore from '$/store/useTeamFlowStore';
import { NODE_NAME_CONFIG, TVFlowNodeNextTypeEnum } from '../config/node.config';

type LooseRecord = Record<string, any>;

interface TeamFlowNodeAssetData extends LooseRecord {
  assetId?: number | string;
  media_list?: LooseRecord[];
  name?: string;
  title?: string;
}

export type TeamFlowAssetResourceType = AssetCreateParams['material_type'];
export type TeamFlowAssetResourceInputType = TeamFlowAssetResourceType | 'text';
export type TeamFlowAssetInputTrackScene =
  | 'workflow'
  | 'media_detail'
  | 'agent_team'
  | 'eraser'
  | 'enhance'
  | 'watermark'
  | 'cutout';

export interface TeamFlowAssetResource {
  assetId?: number | string;
  cover_url?: string;
  coverUrl?: string;
  duration?: unknown;
  ext?: string;
  height?: unknown;
  materialType?: TeamFlowAssetResourceInputType;
  name?: string;
  originUrl?: string;
  resourceType?: TeamFlowAssetResourceInputType;
  sourceId?: string;
  sourceScene?: AssetCreateParams['source_scene'];
  sub_task_id?: string;
  subTaskId?: string;
  suffix?: string;
  task_id?: string;
  taskId?: string;
  thumbnail_url?: string;
  thumbnailUrl?: string;
  title?: string;
  url?: string;
  value?: string;
  watermark_cover_url?: string;
  watermark_url?: string;
  watermarkUrl?: string;
  /** @deprecated 兼容旧调用方；资产创建不再读取或持久化。 */
  compress_url?: string;
  /** @deprecated 兼容旧调用方；资产创建不再读取或持久化。 */
  compressUrl?: string;
  /** @deprecated 兼容旧调用方；资产创建不再读取或持久化。 */
  watermark_compress_url?: string;
  /** @deprecated 兼容旧调用方；资产创建不再读取或持久化。 */
  watermarkCompressUrl?: string;
  width?: unknown;
}

export type AddTeamFlowAssetsToLibraryParams =
  | { nodes: FlowNodeEntity[]; resources?: never; trackScene?: TeamFlowAssetInputTrackScene; }
  | { nodes?: never; resources: TeamFlowAssetResource[]; trackScene?: TeamFlowAssetInputTrackScene; };

interface TeamFlowAssetLibraryTrackItem {
  assetId: string;
  subTaskId: string;
  taskId: string;
  url: string;
}

interface TeamFlowNodeAssetLibraryResult {
  assetIds: Array<number | null>;
  resources: TeamFlowAssetResource[];
}

interface CreateTeamFlowAssetResourceIdsOptions {
  /** 自动补历史记录资源 ID 时不阻塞主流程，失败仅返回 null。 */
  silent?: boolean;
}

/**
 * 将未知历史记录资源 ID 收敛为可用正整数，供复用已有 assetId 时使用。
 *
 * @param {unknown} value
 * @returns {number | null}
 */
export function normalizeTeamFlowAssetId(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}

/**
 * 根据 TeamFlow 节点类型解析写入历史记录时的素材类型，脚本和文本统一按文档处理。
 *
 * @param {string | number} flowNodeType
 * @returns {TeamFlowAssetResourceInputType | null}
 */
export function resolveTeamFlowAssetMaterialTypeFromNodeType(flowNodeType: string | number): TeamFlowAssetResourceInputType | null {
  if (flowNodeType === TVFlowNodeNextTypeEnum.IMAGE_NODE) {
    return 'image';
  }
  if (flowNodeType === TVFlowNodeNextTypeEnum.VIDEO_NODE) {
    return 'video';
  }
  if (flowNodeType === TVFlowNodeNextTypeEnum.AUDIO_NODE) {
    return 'audio';
  }
  if (
    flowNodeType === TVFlowNodeNextTypeEnum.TEXT_NODE
    || flowNodeType === TVFlowNodeNextTypeEnum.SCRIPT_NODE
  ) {
    return 'document';
  }
  return null;
}

/** 从媒体列表中读取首个有效 assetId，供节点顶层兼容字段回写。 */
export function pickFirstTeamFlowMediaAssetId(mediaList: Array<Record<string, unknown>>): number | undefined {
  const media = mediaList.find((item) => {
    return normalizeTeamFlowAssetId(item.assetId) != null || normalizeTeamFlowAssetId(item.asset_id) != null;
  });
  const assetId = normalizeTeamFlowAssetId(media?.assetId) ?? normalizeTeamFlowAssetId(media?.asset_id);
  return assetId ?? undefined;
}

/** 按媒体下标读取已存在的资产 ID，兼容旧数据只把首项写在节点顶层的情况。 */
function getExistingTeamFlowMediaAssetId(
  data: TeamFlowNodeAssetData | undefined,
  media: LooseRecord | undefined,
  index: number,
): number | null {
  return normalizeTeamFlowAssetId(media?.assetId)
    ?? normalizeTeamFlowAssetId(media?.asset_id)
    ?? (index === 0 ? normalizeTeamFlowAssetId(data?.assetId) : null);
}

/** 将未知值解析为正数，避免把空字符串或非法尺寸写进 createAsset。 */
function normalizePositiveNumber(value: unknown, round = false): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return round ? Math.round(n) : n;
}

/** 去掉 URL 查询串后提取文件扩展名。 */
function getUrlExtension(url?: string): string {
  const path = (url || '').split(/[?#]/)[0] ?? '';
  const filename = path.split('/').pop() ?? '';
  const ext = filename.includes('.') ? filename.split('.').pop() : '';
  return ext?.toLowerCase?.().replace(/^\./, '').trim() || '';
}

/** 规范化写入 createAsset.ext 的后缀，去除前导点并统一小写。 */
function normalizeAssetExtension(ext?: string): string {
  return (ext || '').replace(/^\./, '').toLowerCase().trim();
}

/** 文档类节点写入历史记录时生成与实际资源后缀一致的文件名。 */
function getDocumentAssetFileName(data?: { name?: string; title?: string; }, extension = 'md', fallbackName = '文本'): string {
  const rawName = sanitizeFilename(data?.title || data?.name || fallbackName) || fallbackName;
  const ext = normalizeAssetExtension(extension) || 'md';
  const lastDotIndex = rawName.lastIndexOf('.');
  const baseName = lastDotIndex > 0 ? rawName.slice(0, lastDotIndex) : rawName;
  return `${baseName}.${ext}`;
}

/** 文本/脚本节点没有自定义名称时，使用业务节点名作为资产名兜底。 */
function getDocumentNodeFallbackName(flowNodeType: string | number): string {
  if (flowNodeType === TVFlowNodeNextTypeEnum.SCRIPT_NODE) return '脚本';
  return '文本';
}

/** 读取文本/脚本节点资产名，过滤配置 key 兜底，避免直接生成 text_node/script 文件名。 */
function getTeamFlowDocumentNodeAssetTitle(node: FlowNodeEntity, data: TeamFlowNodeAssetData): string {
  const nodeType = node.flowNodeType as TVFlowNodeNextTypeEnum;
  const displayName = getTeamFlowNodeDisplayName(node, data);
  const configName = NODE_NAME_CONFIG[nodeType];
  return displayName && displayName !== configName ? displayName : getDocumentNodeFallbackName(node.flowNodeType);
}

/** 读取 Team Studio 节点当前展示名，优先使用计数器写入的 data.name。 */
function getTeamFlowNodeDisplayName(node: FlowNodeEntity, data: TeamFlowNodeAssetData): string {
  const nodeType = node.flowNodeType as TVFlowNodeNextTypeEnum;
  const displayName = typeof data.name === 'string' && data.name.trim()
    ? data.name.trim()
    : '';
  return displayName || NODE_NAME_CONFIG[nodeType] || 'roboneo_web_node';
}

/** 非空文件名才写入 createAsset.name，保持历史记录展示字段干净。 */
function pickAssetName(name?: string): Pick<AssetCreateParams, 'name'> | object {
  const n = (name || '').trim();
  return n ? { name: n } : {};
}

/** 将外部业务资源类型归一为 createAsset.material_type。 */
function resolveTeamFlowAssetMaterialType(resource: TeamFlowAssetResource): TeamFlowAssetResourceType | null {
  const rawType = resource.materialType || resource.resourceType;
  if (rawType === 'text') {
    return 'document';
  }
  return rawType ?? null;
}

/** 读取资源主 URL，和 createAsset 参数构造保持同一优先级。 */
function resolveTeamFlowAssetResourceUrl(resource: TeamFlowAssetResource): string {
  return (resource.originUrl || resource.url || '').trim();
}

/** 读取添加资产库点击埋点的任务 id，兼容节点媒体与外部结果资源的字段差异。 */
function resolveTeamFlowAssetResourceTaskId(resource: TeamFlowAssetResource): string {
  return String(resource.task_id || resource.taskId || resource.sourceId || '').trim();
}

/** 读取添加资产库点击埋点的子任务 id，兼容下划线与驼峰字段。 */
function resolveTeamFlowAssetResourceSubTaskId(resource: TeamFlowAssetResource): string {
  return String(resource.sub_task_id || resource.subTaskId || '').trim();
}

/** 将资源与入库结果合并为可上报的添加资产库点击埋点条目。 */
function buildTeamFlowAssetLibraryTrackItems(
  resources: TeamFlowAssetResource[],
  assetIds: Array<number | null>,
): TeamFlowAssetLibraryTrackItem[] {
  return resources.reduce<TeamFlowAssetLibraryTrackItem[]>((list, resource, index) => {
    const assetId = normalizeTeamFlowAssetId(assetIds[index]) ?? normalizeTeamFlowAssetId(resource.assetId);
    const url = resolveTeamFlowAssetResourceUrl(resource);
    if (!url) {
      return list;
    }

    list.push({
      assetId: assetId == null ? '' : String(assetId),
      subTaskId: resolveTeamFlowAssetResourceSubTaskId(resource),
      taskId: resolveTeamFlowAssetResourceTaskId(resource),
      url,
    });
    return list;
  }, []);
}

/** TeamFlow 添加到资产库入口点击埋点统一在入库函数内上报，避免按钮/消息/预览入口重复拼字段。 */
function trackTeamFlowAssetsAddedToLibraryInputClick(
  resources: TeamFlowAssetResource[],
  assetIds: Array<number | null>,
  scene: TeamFlowAssetInputTrackScene,
) {
  const items = buildTeamFlowAssetLibraryTrackItems(resources, assetIds);
  if (!items.length) return;

  mtstatTrack('roboneo_asset_input_click', {
    asset_id: items.map((item) => item.assetId).join(','),
    task_id: items.map((item) => item.taskId).join(','),
    sub_task_id: items.map((item) => item.subTaskId).join(','),
    media_url: items.map((item) => item.url).join(','),
    scene,
  });
}

/** 从 TeamFlow store 中读取写入历史记录的公共上下文字段。 */
function getTeamFlowAssetBaseParams(): Pick<AssetCreateParams, 'task_type' | 'room_id'> {
  const { roomId } = useFlowNextStore.getState();
  return {
    task_type: 'agent-team',
    ...(roomId ? { room_id: roomId } : {}),
  };
}

/** 上传无 URL 的文本正文为 markdown，返回上传后的文件信息。 */
function uploadMarkdownContent(value: unknown, fileName: string): Promise<CallbackFiles | null> {
  if (typeof value !== 'string' || !value.trim()) {
    return Promise.resolve(null);
  }

  const blob = new Blob([value], { type: 'text/markdown' });
  const file = blobToFile(blob, fileName);

  return new Promise((resolve, reject) => {
    dealUploadFile({
      files: [file],
      controlFilesOptions: {
        accept: ['text/markdown'],
        importCount: 1,
      },
      isCheckSafe: false,
      location: 'agent_teams_page',
    }, {
      callback: (files) => resolve(files[0] ?? null),
      onUploadError: (_errorList, errorMessage) => reject(errorMessage),
    });
  });
}

/**
 * 根据媒体资源类型和字段构造写入历史记录（createAsset）的请求参数。
 *
 * @param {TeamFlowAssetResource} resource
 * @returns {Promise<AssetCreateParams | null>}
 */
async function buildTeamFlowAssetResourceCreateParams(resource: TeamFlowAssetResource): Promise<AssetCreateParams | null> {
  const base = getTeamFlowAssetBaseParams();
  const materialType = resolveTeamFlowAssetMaterialType(resource);
  if (!materialType) {
    return null;
  }

  const sourceId = resource.sourceId || resource.task_id || resource.taskId;
  const sourceFields = sourceId ? { source_id: sourceId } : {};

  if (materialType === 'document') {
    const uploadFileName = getDocumentAssetFileName(resource);
    const uploaded = resource.url ? null : await uploadMarkdownContent(
      resource.value,
      uploadFileName,
    );
    const url = (resource.url || uploaded?.url || '').trim();
    if (!url) return null;
    const urlExt = getUrlExtension(url);
    const assetExt = urlExt || normalizeAssetExtension(resource.ext || resource.suffix) || 'md';
    const fileName = getDocumentAssetFileName(resource, assetExt);
    return {
      ...base,
      material_type: materialType,
      source_scene: resource.sourceScene || 'import',
      url,
      ext: assetExt,
      ...sourceFields,
      ...pickAssetName(fileName),
    };
  }

  if (materialType === 'image') {
    const url = (resource.originUrl || resource.url || '').trim();
    if (!url) return null;
    const watermarkUrl = (resource.watermark_url || resource.watermarkUrl || '').trim();
    return {
      ...base,
      material_type: materialType,
      ...(resource.sourceScene ? { source_scene: resource.sourceScene } : {}),
      url,
      ...(watermarkUrl ? { watermark_url: watermarkUrl } : {}),
      ...sourceFields,
      ...(normalizeAssetExtension(resource.ext || resource.suffix) || getUrlExtension(url)
        ? { ext: normalizeAssetExtension(resource.ext || resource.suffix) || getUrlExtension(url) }
        : {}),
      ...(normalizePositiveNumber(resource.width, true) ? { width: normalizePositiveNumber(resource.width, true) } : {}),
      ...(normalizePositiveNumber(resource.height, true) ? { height: normalizePositiveNumber(resource.height, true) } : {}),
      ...pickAssetName(resource.name),
    };
  }

  if (materialType === 'video') {
    const url = (resource.originUrl || resource.url || '').trim();
    if (!url) return null;
    const watermarkUrl = (resource.watermark_url || resource.watermarkUrl || '').trim();
    const thumbnailUrl = (
      resource.thumbnail_url
      || resource.thumbnailUrl
      || resource.cover_url
      || resource.coverUrl
      || resource.watermark_cover_url
      || ''
    ).trim() || getVideoCoverUrl(watermarkUrl || url);
    return {
      ...base,
      material_type: materialType,
      ...(resource.sourceScene ? { source_scene: resource.sourceScene } : {}),
      url,
      ...(watermarkUrl ? { watermark_url: watermarkUrl } : {}),
      ...(thumbnailUrl ? { thumbnail_url: thumbnailUrl } : {}),
      ...sourceFields,
      ...(normalizePositiveNumber(resource.width, true) ? { width: normalizePositiveNumber(resource.width, true) } : {}),
      ...(normalizePositiveNumber(resource.height, true) ? { height: normalizePositiveNumber(resource.height, true) } : {}),
      ...(normalizePositiveNumber(resource.duration) ? { duration: normalizePositiveNumber(resource.duration) } : {}),
      ...pickAssetName(resource.name),
    };
  }

  if (materialType === 'audio') {
    const url = (resource.originUrl || resource.url || '').trim();
    if (!url) return null;
    return {
      ...base,
      material_type: materialType,
      ...(resource.sourceScene ? { source_scene: resource.sourceScene } : {}),
      url,
      ...sourceFields,
      ...(normalizePositiveNumber(resource.duration) ? { duration: normalizePositiveNumber(resource.duration) } : {}),
      ...pickAssetName(resource.name),
    };
  }

  if (materialType === 'excel' || materialType === 'ppt') {
    const url = (resource.originUrl || resource.url || '').trim();
    if (!url) return null;
    return {
      ...base,
      material_type: materialType,
      source_scene: resource.sourceScene || 'import',
      url,
      ext: normalizeAssetExtension(resource.ext || resource.suffix) || getUrlExtension(url),
      ...sourceFields,
      ...pickAssetName(resource.name),
    };
  }

  return null;
}

/** 给节点媒体补充已有资产 ID，确保多媒体节点只为缺失项创建新资产。 */
function buildNodeMediaAssetResource(
  data: TeamFlowNodeAssetData,
  media: LooseRecord,
  index: number,
  materialType: TeamFlowAssetResourceInputType,
): TeamFlowAssetResource {
  const existingAssetId = getExistingTeamFlowMediaAssetId(data, media, index);
  return {
    ...media,
    ...(existingAssetId != null ? { assetId: existingAssetId } : {}),
    materialType,
  };
}

/** 根据节点类型和 media_list 提取可入资产库的通用媒体资源列表。 */
function getTeamFlowNodeAssetResources(node: FlowNodeEntity, data: TeamFlowNodeAssetData): TeamFlowAssetResource[] {
  const media = data.media_list?.[0] ?? {};

  if (
    node.flowNodeType === TVFlowNodeNextTypeEnum.TEXT_NODE
    || node.flowNodeType === TVFlowNodeNextTypeEnum.SCRIPT_NODE
  ) {
    const existingAssetId = getExistingTeamFlowMediaAssetId(data, media, 0);
    return [{
      ...media,
      ...(existingAssetId != null ? { assetId: existingAssetId } : {}),
      materialType: 'document',
      title: getTeamFlowDocumentNodeAssetTitle(node, data),
      value: media.value,
    }];
  }

  if (node.flowNodeType === TVFlowNodeNextTypeEnum.IMAGE_NODE) {
    return (Array.isArray(data.media_list) ? data.media_list : [])
      .map((item, index) => buildNodeMediaAssetResource(data, item, index, 'image'));
  }

  if (node.flowNodeType === TVFlowNodeNextTypeEnum.VIDEO_NODE) {
    return (Array.isArray(data.media_list) ? data.media_list : [])
      .map((item, index) => buildNodeMediaAssetResource(data, item, index, 'video'));
  }

  if (node.flowNodeType === TVFlowNodeNextTypeEnum.AUDIO_NODE) {
    const existingAssetId = getExistingTeamFlowMediaAssetId(data, media, 0);
    return [{
      ...media,
      ...(existingAssetId != null ? { assetId: existingAssetId } : {}),
      materialType: 'audio',
    }];
  }

  return [];
}

/** 将资产 ID 按媒体下标回写，并保留顶层首个有效 assetId 兼容旧消费方。 */
function writeAssetIdsToTeamFlowNode(node: FlowNodeEntity, data: TeamFlowNodeAssetData, assetIds: Array<number | null>) {
  const mediaList = Array.isArray(data.media_list) ? data.media_list : [];
  const normalizedAssetIds = assetIds.map(normalizeTeamFlowAssetId);
  const firstAssetId = normalizedAssetIds.find((assetId): assetId is number => assetId != null);
  if (firstAssetId == null) return;
  const nextMediaList = mediaList.map((item, index) => {
    const assetId = normalizedAssetIds[index];
    return assetId != null ? { ...item, assetId } : item;
  });

  node.form?.updateFormValues({
    ...data,
    assetId: firstAssetId,
    media_list: nextMediaList,
  });
}

/** 确保单个节点资源入库并返回资源快照，供批量入口统一上报埋点。 */
async function addSingleTeamFlowNodeAssetsToLibrary(node: FlowNodeEntity): Promise<TeamFlowNodeAssetLibraryResult> {
  const data = (node.toJSON()?.data ?? {}) as TeamFlowNodeAssetData;
  const resources = getTeamFlowNodeAssetResources(node, data);
  if (!resources.length) {
    return {
      assetIds: [null],
      resources: [],
    };
  }

  const assetIds = await createTeamFlowAssetResourceIds(resources);
  writeAssetIdsToTeamFlowNode(node, data, assetIds);
  return {
    assetIds,
    resources,
  };
}

/**
 * 批量将外部传入的媒体资源写入历史记录，调用方需显式传 materialType。
 *
 * @param {TeamFlowAssetResource[]} resources
 * @param {CreateTeamFlowAssetResourceIdsOptions} [options]
 * @returns {Promise<Array<number | null>>}
 */
async function createTeamFlowAssetResourceIds(
  resources: TeamFlowAssetResource[],
  options?: CreateTeamFlowAssetResourceIdsOptions,
): Promise<Array<number | null>> {
  return Promise.all(resources.map(async (resource) => {
    const existingAssetId = normalizeTeamFlowAssetId(resource.assetId);
    if (existingAssetId != null) {
      return existingAssetId;
    }

    const params = await buildTeamFlowAssetResourceCreateParams(resource);
    if (!params) {
      return null;
    }

    try {
      const { asset_id: assetId } = await createAsset(params);
      return assetId;
    } catch (error) {
      if (!options?.silent) {
        throw error;
      }
      console.warn('[TeamFlow registerNodeAsset] createAsset failed', params, error);
      return null;
    }
  }));
}

/**
 * 自动确保资源拥有历史记录资源 ID；用于模型生成、上传等非用户点击场景，不上报「添加到历史记录」点击埋点。
 *
 * @param {TeamFlowAssetResource[]} resources
 * @returns {Promise<Array<number | null>>}
 */
export function ensureTeamFlowAssetResourceIds(resources: TeamFlowAssetResource[]): Promise<Array<number | null>> {
  return createTeamFlowAssetResourceIds(resources, { silent: true });
}

/** 批量确保 TeamFlow 节点已入资产库；多媒体节点会展开成多个返回结果。 */
async function ensureTeamFlowNodeAssetIds(
  nodes: FlowNodeEntity[],
  scene: TeamFlowAssetInputTrackScene,
): Promise<Array<number | null>> {
  const results = await Promise.all(nodes.map((node) => addSingleTeamFlowNodeAssetsToLibrary(node)));
  const assetIds = results.flatMap((result) => result.assetIds);
  const trackResources: TeamFlowAssetResource[] = [];
  const trackAssetIds: Array<number | null> = [];
  results.forEach((result) => {
    if (!result.resources.length) return;
    trackResources.push(...result.resources);
    trackAssetIds.push(...result.assetIds);
  });
  trackTeamFlowAssetsAddedToLibraryInputClick(trackResources, trackAssetIds, scene);
  return assetIds;
}

/** 对外统一入口：参数层面强制 nodes 与 resources 二选一，避免混传。 */
export function addTeamFlowAssetsToLibrary(params: AddTeamFlowAssetsToLibraryParams): Promise<Array<number | null>> {
  if (params.nodes) {
    return ensureTeamFlowNodeAssetIds(params.nodes, params.trackScene ?? 'workflow');
  }
  return createTeamFlowAssetResourceIds(params.resources).then((assetIds) => {
    trackTeamFlowAssetsAddedToLibraryInputClick(params.resources, assetIds, params.trackScene ?? 'agent_team');
    return assetIds;
  });
}
